import type { AttachmentBlockModel } from '@blocksuite/affine/model';
import { Boxed } from '@blocksuite/affine/store';
import { nanoid } from 'nanoid';
import * as Y from 'yjs';

import type {
  PdfEdit,
  PdfEditType,
  PdfRect,
  PdfTextStyle,
} from './types';

type YEdits = Y.Map<Y.Map<unknown>>;

type AddTextOptions = {
  page: number;
  box: PdfRect;
  initialText: string;
  style: PdfTextStyle;
  author?: string;
};

/**
 * Collaborative edit list for one PDF attachment, stored in the
 * attachment block's `pdfEdits` prop as a Y.Map of Y.Maps.
 * All mutations go through Yjs, so edits sync to every collaborator
 * through the regular doc sync channel and participate in undo/redo.
 */
export class PdfEditStore {
  private version = 0;
  private readonly listeners = new Set<() => void>();
  private observedY: YEdits | null = null;
  private unsubscribeProp?: () => void;

  constructor(readonly model: AttachmentBlockModel) {}

  get yEdits(): YEdits | null {
    return this.model.props.pdfEdits?.getValue() ?? null;
  }

  /** Monotonic counter bumped on every remote or local change. */
  getVersion() {
    return this.version;
  }

  subscribe(listener: () => void): () => void {
    if (this.listeners.size === 0) {
      this.start();
    }
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0) {
        this.stop();
      }
    };
  }

  getEditsForPage(page: number): PdfEdit[] {
    return this.getAllEdits().filter(edit => edit.page === page);
  }

  getAllEdits(): PdfEdit[] {
    const yEdits = this.yEdits;
    if (!yEdits) return [];

    const edits: PdfEdit[] = [];
    yEdits.forEach((yEdit, id) => {
      const edit = this.parseEdit(id, yEdit);
      if (edit) edits.push(edit);
    });
    edits.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));
    return edits;
  }

  getEdit(id: string): PdfEdit | null {
    const yEdit = this.yEdits?.get(id);
    return yEdit ? this.parseEdit(id, yEdit) : null;
  }

  /** The live collaborative text of a text edit, or null for other kinds. */
  getText(id: string): Y.Text | null {
    const text = this.yEdits?.get(id)?.get('text');
    return text instanceof Y.Text ? text : null;
  }

  addTextReplace(
    options: AddTextOptions & { rects: PdfRect[] }
  ): string {
    return this.addEdit('text-replace', options.author, yEdit => {
      yEdit.set('page', options.page);
      yEdit.set('rects', options.rects.map(rect => ({ ...rect })));
      yEdit.set('box', { ...options.box });
      yEdit.set('text', new Y.Text(options.initialText));
      yEdit.set('style', { ...options.style });
    });
  }

  addTextAdd(options: AddTextOptions): string {
    return this.addEdit('text-add', options.author, yEdit => {
      yEdit.set('page', options.page);
      yEdit.set('box', { ...options.box });
      yEdit.set('text', new Y.Text(options.initialText));
      yEdit.set('style', { ...options.style });
    });
  }

  addWhiteout(options: {
    page: number;
    rects: PdfRect[];
    author?: string;
  }): string {
    return this.addEdit('whiteout', options.author, yEdit => {
      yEdit.set('page', options.page);
      yEdit.set('rects', options.rects.map(rect => ({ ...rect })));
    });
  }

  addInk(options: {
    page: number;
    points: number[];
    color: string;
    strokeWidth: number;
    author?: string;
  }): string {
    return this.addEdit('ink', options.author, yEdit => {
      yEdit.set('page', options.page);
      yEdit.set('points', [...options.points]);
      yEdit.set('color', options.color);
      yEdit.set('strokeWidth', options.strokeWidth);
    });
  }

  addHighlight(options: {
    page: number;
    rects: PdfRect[];
    color: string;
    author?: string;
  }): string {
    return this.addEdit('highlight', options.author, yEdit => {
      yEdit.set('page', options.page);
      yEdit.set('rects', options.rects.map(rect => ({ ...rect })));
      yEdit.set('color', options.color);
    });
  }

  updateBox(id: string, box: PdfRect) {
    this.updateEdit(id, yEdit => yEdit.set('box', { ...box }));
  }

  updateStyle(id: string, style: Partial<PdfTextStyle>) {
    this.updateEdit(id, yEdit => {
      const current = (yEdit.get('style') ?? {}) as PdfTextStyle;
      yEdit.set('style', { ...current, ...style });
    });
  }

  deleteEdit(id: string) {
    const yEdits = this.yEdits;
    if (!yEdits?.has(id)) return;
    this.model.store.transact(() => {
      yEdits.delete(id);
    });
  }

  private addEdit(
    type: PdfEditType,
    author: string | undefined,
    fill: (yEdit: Y.Map<unknown>) => void
  ): string {
    const id = nanoid();
    this.model.store.transact(() => {
      const yEdits = this.ensureYEdits();
      const yEdit = new Y.Map<unknown>();
      yEdits.set(id, yEdit);
      yEdit.set('type', type);
      yEdit.set('createdAt', Date.now());
      if (author) yEdit.set('author', author);
      fill(yEdit);
    });
    return id;
  }

  private updateEdit(id: string, mutate: (yEdit: Y.Map<unknown>) => void) {
    const yEdit = this.yEdits?.get(id);
    if (!yEdit) return;
    this.model.store.transact(() => {
      mutate(yEdit);
    });
  }

  private ensureYEdits(): YEdits {
    const existing = this.yEdits;
    if (existing) return existing;
    this.model.props.pdfEdits = new Boxed<Y.Map<Y.Map<unknown>>>(new Y.Map());
    const created = this.yEdits;
    if (!created) {
      throw new Error('Failed to create PDF edit storage');
    }
    return created;
  }

  private parseEdit(id: string, yEdit: Y.Map<unknown>): PdfEdit | null {
    const type = yEdit.get('type') as PdfEditType | undefined;
    const base = {
      id,
      page: (yEdit.get('page') as number) ?? 0,
      author: yEdit.get('author') as string | undefined,
      createdAt: yEdit.get('createdAt') as number | undefined,
    };

    switch (type) {
      case 'text-replace': {
        const text = yEdit.get('text');
        if (!(text instanceof Y.Text)) return null;
        return {
          ...base,
          type,
          rects: (yEdit.get('rects') as PdfRect[]) ?? [],
          box: yEdit.get('box') as PdfRect,
          text,
          style: yEdit.get('style') as PdfTextStyle,
        };
      }
      case 'text-add': {
        const text = yEdit.get('text');
        if (!(text instanceof Y.Text)) return null;
        return {
          ...base,
          type,
          box: yEdit.get('box') as PdfRect,
          text,
          style: yEdit.get('style') as PdfTextStyle,
        };
      }
      case 'whiteout':
        return { ...base, type, rects: (yEdit.get('rects') as PdfRect[]) ?? [] };
      case 'ink':
        return {
          ...base,
          type,
          points: (yEdit.get('points') as number[]) ?? [],
          color: (yEdit.get('color') as string) ?? '#000000',
          strokeWidth: (yEdit.get('strokeWidth') as number) ?? 2,
        };
      case 'highlight':
        return {
          ...base,
          type,
          rects: (yEdit.get('rects') as PdfRect[]) ?? [],
          color: (yEdit.get('color') as string) ?? '#ffd54f',
        };
      default:
        return null;
    }
  }

  private readonly onYChange = () => {
    this.notify();
  };

  private notify() {
    this.version++;
    this.listeners.forEach(listener => listener());
  }

  private start() {
    this.attach(this.yEdits);
    // Re-attach when the boxed prop is created (locally or by a remote peer).
    this.unsubscribeProp = this.model.props.pdfEdits$.subscribe(() => {
      const current = this.yEdits;
      if (current !== this.observedY) {
        this.attach(current);
        this.notify();
      }
    });
  }

  private stop() {
    this.unsubscribeProp?.();
    this.unsubscribeProp = undefined;
    this.attach(null);
  }

  private attach(yEdits: YEdits | null) {
    this.observedY?.unobserveDeep(this.onYChange);
    this.observedY = yEdits;
    yEdits?.observeDeep(this.onYChange);
  }
}
